import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Slide, toast, ToastContainer } from "react-toastify";
import { groupsManagement } from "../../../../../../hooks/api/GroupsManagement";
import { useUserToken } from "../../../../../../hooks/core/UserToken";
import { Textarea } from "../../../../../../themes/Default/components/Form/components/Textarea";
import { roles } from "../../../../../../services/utils/Roles";

const ProgressNotes = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const { groupProgressId } = useParams();
    const { groupId } = useParams();
    const { getDecoded } = useUserToken()[0];
    const decodedToken = getDecoded();

    const {useProgressNoteListMutation, useAddProgressNoteMutation, useViewGroupMutation} = groupsManagement;
    const [notesList] = useProgressNoteListMutation();
    const [addNote] = useAddProgressNoteMutation();
    const [hasTests, setHasTests] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [viewGroup] = useViewGroupMutation();

    const [notes, setNotes] = useState();

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        { title: t('groups.progress'),isActive: true },
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const gotoEdit = (groupProgressNoteId) => {
        navigate("/groups/" + groupId + "/progress/" + groupProgressId + "/notes/" + groupProgressNoteId + "/edit" );
    };

    const onSaved = (values) => {
        const body = {...values};
        addNote({body, groupProgressId}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadNotes();
            }
        });
    };

    const loadNotes = () => {
        notesList({groupProgressId}).then((result) => {
            setNotes(result?.data?.records);
        });
    };

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasPlan(result?.data?.hasPlan);
        });
        loadNotes();
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/"+ groupId +"/progress/"}/>
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("groups.progress")} />
                    <Link title={t("groups.progressNotes")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        note: "", 
                    }}
                    onSubmit={(values, { resetForm }) => {
                        onSaved(values);
                        resetForm();
                    }}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea 
                                        name="note" 
                                        label={t('groups.progressNote')} 
                                        rows={5}
                                        value={values.note} 
                                        onChange={handleChange} 
                                        required={true} 
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
                <br />
                <List>
                    <ListHeader>
                        <ListTitle 
                        />
                    </ListHeader>
                    <ListBody>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell><span>{t('groups.progressNote')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Datum')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Vreme')}</span></TableHeadCell>
                                <TableHeadCell><span>{t('Autor')}</span></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {notes?.length > 0 &&
                            notes?.map((note, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableDataCell>
                                            {decodedToken?.id === note.createdBy ? (
                                                <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(note.id)}/>
                                            ) : null}
                                        </TableDataCell>
                                        <TableDataCell>{note.note}</TableDataCell>
                                        <TableDataCell>{note.date}</TableDataCell>
                                        <TableDataCell>{note.time}</TableDataCell>
                                        <TableDataCell>{note.createdByFullName}</TableDataCell>
                                        <TableDataCell></TableDataCell>
                                    </TableRow>
                                )
                        })}
                        </TableBody>
                    </ListBody>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default ProgressNotes;